import React, { useEffect, useState } from 'react';
import { Box, FormControl, FormControlLabel, FormLabel, IconButton, MenuItem, Radio, RadioGroup, Select, Typography } from '@material-ui/core';
import { useTranslation } from "react-i18next";
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';

import { MobileButton } from "../../../components";

import { arrayOfNumbers } from "../../../services/util.service";

import useStyles from "./Page3.style";

const PregnancyQuestionnaire = props => {
    const classes = useStyles();
    const { t } = useTranslation();

    const currentYear = new Date(Date.now()).getFullYear();
    const firstMenstruationAge = (localStorage.getItem('firstMenstruationAge') && parseInt(localStorage.getItem('firstMenstruationAge')))
        || props.firstMenstruationYear;

    return (
        <>
            <Box className={classes.pregnancyQuestionnaire}>
                <Box className={classes.pregnancyQuestionnaireHeader}>
                    <span>{t('common.substantive.pregnancy')} #{props.index + 1}</span>
                    <IconButton aria-label="delete" color="inherit" onClick={props.onPregnancyRemove}>
                        <DeleteIcon />
                    </IconButton>
                </Box>
                <Box className={classes.pregnancyQuestionnaireBody}>
                    <Box className={classes.pregnancyQuestionnaireQuestion}>
                        {t('repliQuestionnaire.general.pregnancyYear')}
                    </Box>
                    <FormControl variant="outlined" fullWidth>
                        <Select value={props.data.year} onChange={props.onPregnancyYearChange}>
                            <MenuItem key={0} value={0}></MenuItem>
                            {arrayOfNumbers(firstMenstruationAge, currentYear - firstMenstruationAge + 1)
                                .map(year => <MenuItem key={year} value={year}>{year}</MenuItem>)
                            }
                        </Select>
                    </FormControl>
                    <FormControl component="fieldset">
                        <FormLabel>{t('common.substantive.intention')}</FormLabel>
                        <RadioGroup aria-label="preganancyIntentions" value={props.data.intention} onChange={props.onPregnancyIntentionChange}>
                            <FormControlLabel value="intended" control={<Radio />} label={t('repliQuestionnaire.label.intention.intended')} />
                            <FormControlLabel value="unintended" control={<Radio />} label={t('repliQuestionnaire.label.intention.unintended')} />
                            <FormControlLabel value="mistimed" control={<Radio />} label={t('repliQuestionnaire.label.intention.mistimed')} />
                        </RadioGroup>
                    </FormControl>
                    <FormControl component="fieldset">
                        <FormLabel>{t('common.substantive.outcome')}</FormLabel>
                        <RadioGroup aria-label="preganancyOutcome" value={props.data.outcome} onChange={props.onPregnancyOutcomeChange}>
                            <FormControlLabel value="liveBirth" control={<Radio />} label={t('repliQuestionnaire.label.outcome.liveBirth')} />
                            <FormControlLabel value="miscarriage" control={<Radio />} label={t('repliQuestionnaire.label.outcome.miscarriage')} />
                            <FormControlLabel value="ectopicTubal" control={<Radio />} label={t('repliQuestionnaire.label.outcome.ectopicTubal')} />
                            <FormControlLabel value="abortion" control={<Radio />} label={t('repliQuestionnaire.label.outcome.abortion')} />
                        </RadioGroup>
                    </FormControl>
                    {
                        props.data.outcome === 'liveBirth' &&
                        <>
                            <Box className={classes.pregnancyQuestionnaireQuestion}>
                                # {t('repliQuestionnaire.label.outcome.liveBirth')}
                            </Box>
                            <FormControl variant="outlined" fullWidth>
                                <Select value={props.data.liveBirthQty} onChange={props.onPregnancyLiveBirthQtyChange}>
                                    {
                                        [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(
                                            qty => <MenuItem key={qty} value={qty}>{qty}</MenuItem>
                                        )
                                    }
                                </Select>
                            </FormControl>
                            <Box className={classes.pregnancyQuestionnaireQuestion}>
                                # {t('repliQuestionnaire.label.stillbirth')}
                            </Box>
                            <FormControl variant="outlined" fullWidth>
                                <Select value={props.data.stillbirthQty} onChange={props.onPregnancyStillbirthQtyChange}>
                                    {
                                        [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(
                                            qty => <MenuItem key={qty} value={qty}>{qty}</MenuItem>
                                        )
                                    }
                                </Select>
                            </FormControl>
                            <Box className={classes.pregnancyQuestionnaireQuestion}>
                                # {t('repliQuestionnaire.label.placedAdoption')}
                            </Box>
                            <FormControl variant="outlined" fullWidth>
                                <Select value={props.data.placedAdoptionQty} onChange={props.onPregnancyPlacedAdoptionQtyChange}>
                                    {
                                        [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(
                                            qty => <MenuItem key={qty} value={qty}>{qty}</MenuItem>
                                        )
                                    }
                                </Select>
                            </FormControl>
                        </>
                    }
                </Box>
            </Box>
        </>
    );
};

const Page3 = props => {
    const classes = useStyles();
    const { t } = useTranslation();

    const [pregnancyData, setPregnancyData] = useState([]);

    useEffect(() => {
        const localStoragePregnancyData = localStorage.getItem('pregnancies') && JSON.parse(localStorage.getItem('pregnancies'));
        const propsPregnancyData = props.profile && props.profile.pregnancies;
        const storedPregnancyData = localStoragePregnancyData || propsPregnancyData;

        if (storedPregnancyData) setPregnancyData(storedPregnancyData);
    }, [props.profile]);

    const handleAddPregnancy = e => {
        let newArray = [...pregnancyData, {
            year: '',
            intention: '',
            outcome: '',
            liveBirthQty: 0,
            stillbirthQty: 0,
            placedAdoptionQty: 0
        }];
        setPregnancyData(newArray);
        localStorage.setItem('pregnancies', JSON.stringify(newArray));
    };

    const handleRemovePregnancy = index => e => {
        let remainingData = [...pregnancyData];
        remainingData.splice(index, 1)
        setPregnancyData(remainingData);
        localStorage.setItem('pregnancies', JSON.stringify(remainingData));
    };

    const handlePregnancyDataChange = (index, field) => e => {
        let modifiedData = pregnancyData.map(
            (data, i) => {
                if (i === index) data[field] = e.target.value;
                return data;
            }
        );
        setPregnancyData(modifiedData);
        localStorage.setItem('pregnancies', JSON.stringify(modifiedData));
    };

    const handlePregnancyOutcomeDataChange = index => e => {
        let modifiedData = pregnancyData.map((data, i) => i === index ?
            {
                ...data,
                outcome: e.target.value,
                liveBirthQty: 0,
                stillbirthQty: 0,
                placedAdoptionQty: 0
            }
            : data);
        setPregnancyData(modifiedData);
        localStorage.setItem('pregnancies', JSON.stringify(modifiedData));
    };

    return (
        <>
            <Box className={classes.textSection}>
                <Typography component="div" className={classes.text}>
                    {t('repliQuestionnaire.general.preganciesSection')}
                </Typography>
            </Box>
            {
                pregnancyData.map((data, i) =>
                    <PregnancyQuestionnaire
                        key={i}
                        index={i}
                        data={data}
                        firstMenstruationYear={props.profile.firstMenstruationYear}
                        onPregnancyRemove={handleRemovePregnancy(i)}
                        onPregnancyYearChange={handlePregnancyDataChange(i, 'year')}
                        onPregnancyIntentionChange={handlePregnancyDataChange(i, 'intention')}
                        onPregnancyOutcomeChange={handlePregnancyOutcomeDataChange(i)}
                        onPregnancyLiveBirthQtyChange={handlePregnancyDataChange(i, 'liveBirthQty')}
                        onPregnancyStillbirthQtyChange={handlePregnancyDataChange(i, 'stillbirthQty')}
                        onPregnancyPlacedAdoptionQtyChange={handlePregnancyDataChange(i, 'placedAdoptionQty')}
                    />
                )
            }
            <MobileButton
                label={t('repliQuestionnaire.label.addPregnancy')}
                startIcon={<AddIcon />}
                onClick={handleAddPregnancy}
            />
        </>
    );
};

export default Page3;